/**
 * Cookie/tracking consent gate.
 *
 * Use anywhere you need to fire a third-party tracker (Meta Pixel,
 * Google Analytics, TikTok Pixel, etc.). The gate respects the visitor's
 * choice from the CookieConsent banner — when they reject non-essential
 * cookies, your initializer never runs.
 *
 *   import { onConsent, hasConsent } from '../lib/consentGate';
 *
 *   onConsent(() => {
 *     // Loaded only when the visitor has accepted.
 *     // Re-runs automatically if they later accept via "Manage cookies".
 *     window.fbq?.('init', PIXEL_ID);
 *     window.fbq?.('track', 'PageView');
 *   });
 *
 *   // Inside a click handler / page-view tracker:
 *   if (hasConsent()) window.fbq?.('track', 'AddToCart', {...});
 *
 * The helper is idempotent — passing the same callback twice only runs it once.
 */

const KEY = 'cookieConsent';
const seen = new WeakSet();

export function hasConsent() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(KEY) === 'accepted';
}

/**
 * Run `cb` immediately if consent was already granted; otherwise wait for
 * the CookieConsent banner to dispatch `cookieconsent:change` with detail
 * 'accepted'.
 */
export function onConsent(cb) {
  if (typeof window === 'undefined') return;
  if (seen.has(cb)) return;
  seen.add(cb);

  if (hasConsent()) { cb(); return; }

  const handler = (e) => {
    if (e.detail === 'accepted') {
      window.removeEventListener('cookieconsent:change', handler);
      cb();
    }
  };
  window.addEventListener('cookieconsent:change', handler);
}

/** Re-open the consent banner so the visitor can change their mind. */
export function reopenBanner() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('cookieconsent:show'));
}
